<template>
  <div class="bg-black h-full">
    <div class="flex bg-black h-full">
      <div class="flex flex-col flex-grow relative">
        <header
          class="
            header
            absolute
            left-0
            right-0
            top-0
            overflow-visible
            text-white
            px-3
            z-30
            transition-all
            duration-300
            ease-in-out
            hover-gradient
            hover:opacity-100
            opacity-60
          "
          :class="{ hidden: !showHeader }"
        >
          <!-- Header -->
          <SessionHeader
            :endPhase="endPhase"
            :backToPreviousPhase="backToPreviousPhase"
            :muteParticipants="muteAllParticipants"
            :turnOffSmartGlass="sessionStore.deviceMessageSender.turnOffSmartGlass"
            class="step-control-panel"
          />
          <DebugPanel />
        </header>
        <main class="relative overflow-hidden" v-if="sessionStore.mediaPermissionError">
          <PermissionsNotGrantedOverlay />
        </main>
        <main
          class="bg-black flex items-center justify-center overflow-hidden"
          v-else-if="!sessionStore.phasesDataInitialized"
        >
          <svg
            class="animate-spin -ml-1 mr-3 h-6 w-6 text-gray-150"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </main>
        <template v-else>
          <main v-if="sessionStore.isGroupCallStarted" class="overflow-hidden">
            <GroupCallView />
          </main>
          <main class="relative overflow-hidden" v-else-if="sessionStore.isConsultationCallStarted">
            <ConsultationCallView
              :muteMedia="muteMedia"
              :clinicianDevices="clinicianDevices"
              @showHeader="showHeader = $event"
            />
          </main>
          <main
            v-else-if="sessionStore.isSessionEnded"
            class="flex justify-center items-center overflow-hidden"
          >
            <SessionEnd :isClinician="true" :sessionId="session.unique_id" />
          </main>
          <main
            class="
              flex flex-col
              justify-center
              mx-auto
              py-4
              pt-8
              2xl:pt-4
              relative
              text-center text-white
              w-full
              overflow-hidden
            "
            style="max-width: calc(100vw - 330px)"
            v-else
          >
            <PermissionsNotGrantedOverlay v-if="sessionStore.mediaPermissionError" />

            <section class="my-4 flex justify-center items-center gap-5">
              <h1 class="prose-xl font-bold leading-5">Waiting to start</h1>
              <h2 class="flex justify-center items-center space-x-2">
                <span
                  class="
                    inline-flex
                    justify-center
                    items-center
                    prose-sm
                    font-bold
                    w-10
                    h-10
                    border-2
                    rounded-full
                  "
                >
                  {{ sessionStore.currentPhase }}
                </span>
                <span class="prose-2xl font-medium">
                  {{ sessionStore.currentPhaseData?.['title'] }}
                </span>
              </h2>
            </section>
            <section>
              <PreGroupcallView
                v-if="sessionStore.currentPhase === VICE_SESSION_PHASES.DEBRIEF"
                class="camera-preview"
              >
                <template v-slot:camera-preview>
                  <StreamPlayerLocal
                    name="Clinician locl preview SPLN"
                    class="camera"
                    :isMinimizable="false"
                  />
                </template>
              </PreGroupcallView>

              <template
                v-else-if="
                  sessionStore.currentPhase === VICE_SESSION_PHASES.INTRODUCTION ||
                  sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION
                "
              >
                <MultiCameraPreviewGrid class="step-multi-camera" />
              </template>
            </section>
            <div class="w-full flex mt-4 justify-center">
              <div
                v-if="showNewPhaseLoaderFlag"
                class="
                  text-white
                  leading-6
                  font-medium
                  bg-vice-teal-700
                  rounded
                  inline-flex
                  mt-6
                  py-2
                  px-3
                  2xl:py-4
                  2xl:px-6
                "
              >
                <div class="loader"></div>
              </div>
              <button
                :data-cy="`start-` + sessionStore.currentPhaseData['title'].toLowerCase()"
                class="
                  text-white
                  leading-6
                  font-medium
                  bg-vice-teal-700
                  rounded
                  inline-flex
                  mt-6
                  py-2
                  px-3
                  2xl:py-4
                  2xl:px-6
                "
                :class="[
                  sessionStore.currentPhaseData['title'].toLowerCase() == 'introduction'
                    ? 'step-start-introduction'
                    : sessionStore.currentPhaseData['title'].toLowerCase() == 'consultation'
                    ? 'step-start-consultation'
                    : 'step-start-debrief',
                ]"
                v-if="jitsiRoom && showNewPhaseButtonFlag"
                @click="setNextPhase(sessionStore.currentPhaseData)"
              >
                <VideoCameraIcon class="h-6 w-6 mr-2" />
                Start
                {{ sessionStore.currentPhaseData['title'] }}
              </button>
            </div>
          </main>
        </template>
        <!-- Footer -->
        <SessionFooter
          :endPhase="endPhase"
          :jitsiRoom="jitsiRoom"
          :defaultDevices="clinicianDevices"
          :turnOffSmartGlass="sessionStore.deviceMessageSender.turnOffSmartGlass"
        />
      </div>
      <SessionSidebar
        @lowerHand="lowerHand"
        :isClinician="true"
        :kickParticipant="kickParticipant"
        :muteSingleParticipant="muteSingleParticipant"
      />
    </div>
  </div>
  <BrowserCheckModal></BrowserCheckModal>
  <TwoFactorAuthModal></TwoFactorAuthModal>
</template>

<script>
import { ref, onUnmounted, onMounted, watch, computed } from 'vue';
import { VideoCameraIcon } from '@heroicons/vue/solid';
import { Intercom } from '@intercom/messenger-js-sdk';
import { useSetupSessionData } from '@/Composables/useSetupSessionData';
import {
  DISPLAY_NAME,
  INTERCOM_APP_ID,
  SESSION_CALL_HELPERS,
  VICE_SESSION_PHASES,
} from '@/Utils/vice-constants';
import PreGroupcallView from '@/Components/Clinician/PreGroupcallView.vue';
import MultiCameraPreviewGrid from '@/Components/Clinician/MultiCameraPreviewGrid.vue';
import StreamPlayerLocal from '@/Components/Session/Grid/StreamPlayerLocal.vue';
import SessionEnd from '@/Components/Modals/SessionEnd.vue';
import SessionHeader from '@/Components/Session/Header/SessionHeader.vue';
import SessionFooter from '@/Components/Session/Footer/SessionFooter.vue';
import SessionSidebar from '@/Components/Session/Sidebar/SessionSidebar.vue';
import DebugPanel from '@/Components/Session/DebugPanel.vue';
import JitsiService from '@/Services/JitsiService';
import useSendDeviceMessage from '@/Composables/useSendDeviceMessage';
import useUpdateMediaDevicesList from '@/Composables/useUpdateMediaDevicesList';
import useJitsiCommands from '@/Composables/useJitsiCommands';
import PermissionsNotGrantedOverlay from '@/Components/Session/Overlays/PermissionsNotGrantedOverlay.vue';
import useSessionHandlers from '@/Composables/useSessionHandlers';
import GroupCallView from '@/Components/Clinician/GroupCallView.vue';
import ConsultationCallView from '@/Components/Clinician/ConsultationCallView.vue';
import useTourManager from '@/Composables/useTourManager';
import { clinicianSessionTourSteps, clinicianSessionTourConsultationSteps } from '@/Data/tourSteps';
import BrowserCheckModal from '@/Components/Modals/BrowserCheckModal.vue';
import TwoFactorAuthModal from '@/Components/Modals/TwoFactorAuthModal.vue';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import useExternalCameraComposable from '@/Composables/useExternalCameraComposable';

const clinicianSessionTour = useTourManager('clinicianSessionTour', clinicianSessionTourSteps);

const clinicianSessionTourConsultation = useTourManager(
  'clinicianSessionTourConsultation',
  clinicianSessionTourConsultationSteps,
);

export default {
  name: 'SessionCallClinician',
  components: {
    SessionEnd,
    DebugPanel,
    SessionHeader,
    SessionFooter,
    SessionSidebar,
    VideoCameraIcon,
    PreGroupcallView,
    StreamPlayerLocal,
    MultiCameraPreviewGrid,
    PermissionsNotGrantedOverlay,
    GroupCallView,
    ConsultationCallView,
    BrowserCheckModal,
    TwoFactorAuthModal,
  },
  setup() {
    const { sessionStore, pageProps, session } = useSetupSessionData();
    const sessionId = session.unique_id.replace(/-/g, '');
    sessionStore.deviceMessageSender = useSendDeviceMessage();
    sessionStore.snapshotsAllowed = !!pageProps.session.university?.snapshot_feature;

    const multiCameraDevicesStore = useLocalStreamingDevicesStore();
    multiCameraDevicesStore.clinicianDevicesForSession = pageProps.clinicianDevicesForSession;

    const clinicianDevices = computed(() => multiCameraDevicesStore.clinicianDevicesForSession);
    const jitsiRoom = ref(null);
    const disconnect = ref(null);
    const { commands, events, helpers } = useSessionHandlers();

    // New Phase button and loader flags
    const showNewPhaseButtonFlag = ref(false);
    const showNewPhaseLoaderFlag = ref(true);
    const showHeader = ref(true);

    // TODO: Different ideas are welcome.
    // I want to make sendJitsiCommand globally available so I'll add it to
    // the sessionStore. Any other suggestion is welcome.
    const sendJitsiCommand = useJitsiCommands(jitsiRoom);
    sessionStore.jitsiCommands = sendJitsiCommand;
    const isClinician = pageProps.isClinician;

    // Initialize the device update listener
    useUpdateMediaDevicesList();

    // Todo: Fix the inconsistencies
    const handlePageExit = (e) => {
      if (sessionStore.currentPhase !== VICE_SESSION_PHASES.DEBRIEF) {
        e.returnValue = 'Are you sure you want to close this page?';
      }
    };
    window.addEventListener('beforeunload', handlePageExit);
    window.addEventListener('pagehide', handlePageExit);
    /**
     * Start a new chat thread for the session or get existing messages
     */
    onMounted(async () => {
      document.title = session.title;
      sessionStore.checkForPermissions();

      helpers[SESSION_CALL_HELPERS.HANDLE_NAVIGATION](false);

      JitsiService()
        .makeConnection(sessionId, events, commands)
        .then((jitsiInstance) => {
          jitsiRoom.value = jitsiInstance.room;
          disconnect.value = jitsiInstance.disconnect;
        });

      helpers[SESSION_CALL_HELPERS.INITIALIZE_CHAT_MESSAGES]();
      helpers[SESSION_CALL_HELPERS.UPDATE_SESSION_ANALYTICS]();
      setTimeout(() => {
        clinicianSessionTour.startTour();
      }, 500);

      showNewPhaseButtonLoader();

      Intercom({
        app_id: INTERCOM_APP_ID,
        user_id: sessionStore.authUser.unique_id,
        name: sessionStore.authUser.name,
        email: sessionStore.authUser.email,
        user_hash: sessionStore.authUser.user_hash,
        horizontal_padding: 20,
        vertical_padding: 20,
        hide_default_launcher: true,
      });
    });

    // Stop watching after devicesInitialized
    const unwatch = watch(
      () => multiCameraDevicesStore.devicesInitialized,
      (value) => {
        if (value) {
          useExternalCameraComposable().setMainCamera();
          unwatch();
        }
      },
    );

    watch(jitsiRoom, (newJitsiRoom) => {
      if (newJitsiRoom) {
        initializeRoom();
      }
    });

    onUnmounted(async () => {
      window.removeEventListener('beforeunload', handlePageExit);
      window.removeEventListener('pagehide', handlePageExit);
      try {
        await disconnect.value();
      } catch (error) {
        console.error(error);
      }
    });

    const initializeRoom = () => {
      jitsiRoom.value.setDisplayName(DISPLAY_NAME.NAVIGATION_USER);
      jitsiRoom.value.setLocalParticipantProperty('isNavigationUser', true);
      jitsiRoom.value.join();
      sessionStore.navigatorUserId = jitsiRoom.value.myUserId();
    };

    const resetSession = () => helpers[SESSION_CALL_HELPERS.RESET_SESSION]();

    // Set 5 seconds timeout before each phase starts. Show loader during
    // this 5 seconds and then - show new phase start button
    const showNewPhaseButtonLoader = () => {
      showNewPhaseButtonFlag.value = false;
      showNewPhaseLoaderFlag.value = true;
      setTimeout(() => {
        showNewPhaseButtonFlag.value = true;
        showNewPhaseLoaderFlag.value = false;
      }, 5000);
    };

    const endPhase = (endingPhase, isForcedEnd = false) => {
      helpers[SESSION_CALL_HELPERS.CLINICIAN_END_PHASE](endingPhase, isForcedEnd, sendJitsiCommand);
      if (sessionStore.currentPhase === VICE_SESSION_PHASES.CONSULTATION) {
        clinicianSessionTourConsultation.startTour();
      }
      showNewPhaseButtonLoader();
    };

    const backToPreviousPhase = (currentPhase, isForcedEnd = false) => {
      helpers[SESSION_CALL_HELPERS.CLINICIAN_BACK_TO_PREVIOUS_PHASE](
        currentPhase,
        isForcedEnd,
        sendJitsiCommand,
      );
      showNewPhaseButtonLoader();
    };

    const setNextPhase = (activePhase) => {
      helpers[SESSION_CALL_HELPERS.CLINICIAN_SET_NEXT_PHASE](activePhase, sendJitsiCommand);
    };

    return {
      session,
      sessionId,
      jitsiRoom,
      showHeader,
      isClinician,
      sessionStore,
      clinicianDevices,
      showNewPhaseButtonFlag,
      showNewPhaseLoaderFlag,
      endPhase,
      resetSession,
      setNextPhase,
      backToPreviousPhase,
      VICE_SESSION_PHASES,
      BrowserCheckModal,
      muteMedia: sendJitsiCommand.muteMedia,
      lowerHand: sendJitsiCommand.toggleRaiseHand,
      kickParticipant: sendJitsiCommand.kickParticipant,
      muteAllParticipants: sendJitsiCommand.muteAllParticipants,
      muteSingleParticipant: sendJitsiCommand.muteSingleParticipant,
    };
  },
};
</script>

<style lang="scss" scoped>
.header {
  height: 96px;
}

.hover-gradient {
  background: transparent;
}

.hover-gradient:hover {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
}

main {
  height: 100%;
}

.loader {
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 10rem;
  background: linear-gradient(#000000, #ffffff, #ffffff);
  box-shadow: 0 0px 100px -50px black;
  animation: animate 1s linear infinite;
}

.loader::before {
  position: absolute;
  content: '';
  background: rgba(15, 118, 110, var(--tw-bg-opacity));
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: 80%;
  border-radius: 10rem;
}

@keyframes animate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@keyframes l23 {
  100% {
    transform: rotate(1turn);
  }
}

.min-w-main {
  min-width: 1064px;
}
</style>
